import { useContext, useEffect, useState } from "react";
import CRUDTabTableModal from "../components/CRUDTabTableModal";
import DeliverableConfigurationModal from "../components/Modal/DeliverableConfigurationModal";
import TaskConfigurationModal from "../components/Modal/TaskConfigurationModal";
import { HomeContext } from "./Home";
import Emitter from "../utils/emitter";

function ProgrammeSettingsWorkflowConfig({
	currentTemplateData,
	tabKey,
	formOption,
}: {
	currentTemplateData: any;
	tabKey: string;
	formOption: string;
}) {
	const { userInfo }: any = useContext(HomeContext);

	const [phaseTaskRecord, setPhaseTaskRecord] = useState<any>(null);
	const [deliverableRecord, setDeliverableRecord] = useState<any>(null);
	const [allCampaignTasks, setAllCampaignTasks] = useState<any>([]);
	const [taskModalOpen, setTaskModalOpen] = useState<any>(false);
	const [deliverableModalOpen, setDeliverableModalOpen] = useState<any>(false);

	// State for items configuration
	const [itemsFormOption, setItemsFormOption] = useState<string>(
		tabKey === "well_programme_template"
			? "template"
			: formOption === "general_multi_asset_template_project"
				? "multi_asset_project_template_phase"
				: "multi_asset_operation_template_phase"
	);
	const [itemsTableAuditLog, setItemsTableAuditLog] = useState<any>({
		identifierKeys: {},
		label: `Phases and tasks`,
		type: "phase_template",
		auditlogFilter: [
			{
				level: "phase_template",
				event_type: { $in: ["add", "delete", "reorder"] },
				"ref_tags.well_programme_template_id": currentTemplateData?._id,
			},
			{
				level: "task_template",
				event_type: { $in: ["add", "delete"] },
				"ref_tags.well_programme_template_id": currentTemplateData?._id,
			},
		],
	});

	useEffect(() => {
		if (currentTemplateData) {
			let taskData = [];

			if (formOption === "general") {
				taskData = currentTemplateData?.phases
					?.map((element: any) => [...(element?.tasks ?? [])])
					.flat(Infinity);
			} else if (formOption === "general_multi_asset_template_project") {
				const phaseTasks =
					currentTemplateData?.phases
						?.map((element: any) => [...(element?.tasks ?? [])])
						.flat(Infinity) ?? [];

				const operationTasks =
					currentTemplateData?.operations
						?.map((element: any) => [...(element?.tasks ?? [])])
						.flat(Infinity) ?? [];

				taskData = [...phaseTasks, ...operationTasks];
			} else if (formOption === "general_multi_asset_template_operation") {
				taskData = [...currentTemplateData?.allCampaignTasks];
			}

			// sorting the task data ascending
			if (taskData?.length > 0) {
				taskData.sort((a: any, b: any) => {
					return parseFloat(a.task_number) - parseFloat(b.task_number);
				});
			}

			setAllCampaignTasks(taskData);

			if (phaseTaskRecord) {
				if (formOption === "general_multi_asset_template_operation") {
					let task = currentTemplateData?.tasks?.find(
						(element: any) => element._id === phaseTaskRecord?.task?._id
					);
					setPhaseTaskRecord({
						...currentTemplateData,
						task: task,
					});
				} else {
					const currentPhaseRecord = currentTemplateData?.phases?.find(
						(element: any) => element._id === phaseTaskRecord._id
					);
					let task = currentPhaseRecord?.tasks?.find(
						(element: any) => element._id === phaseTaskRecord?.task?._id
					);
					setPhaseTaskRecord({
						...currentPhaseRecord,
						task: task,
					});
				}
			}
		}
	}, [currentTemplateData, formOption]);

	useEffect(() => {
		Emitter.on("taskModalOpen", (payload: any) => {
			console.log("taskModalOpen: ", payload);
			setPhaseTaskRecord(payload);
			setTaskModalOpen(true);
		});

		return () => {
			Emitter.remove("taskModalOpen");
		};
	}, []);

	useEffect(() => {
		if (tabKey === "well_programme_template") {
			setItemsFormOption("template");
			setItemsTableAuditLog({
				identifierKeys: {},
				label: `Phases and tasks`,
				type: "phase_template",
				auditlogFilter: [
					{
						level: "phase_template",
						event_type: { $in: ["add", "delete", "reorder"] },
						"ref_tags.well_programme_template_id": currentTemplateData?._id,
					},
					{
						level: "task_template",
						event_type: { $in: ["add", "delete"] },
						"ref_tags.well_programme_template_id": currentTemplateData?._id,
					},
				],
			});
		} else {
			setItemsFormOption(
				formOption === "general_multi_asset_template_project"
					? "multi_asset_project_template_phase"
					: "multi_asset_operation_template_phase"
			);
			setItemsTableAuditLog({});
		}
	}, [tabKey, currentTemplateData?._id]);

	const items = [
		{
			label: "Phases and tasks",
			key: "phase",
			formOption: itemsFormOption,
			directoryName: "Phases and tasks directory",
			configurationName: "Phase configuration",
			sortKey: "sequence_id",
			tableAuditLog:
				formOption === "general_multi_asset_template_operation"
					? null
					: itemsTableAuditLog,
			showSideContainerAuditLog:
				formOption === "general_multi_asset_template_operation" ? false : true,
		},
	];

	const handleTaskFormUpdate = (payload: any) => {
		if (payload) {
			setPhaseTaskRecord({ ...phaseTaskRecord, task: payload });
		}
	};

	const handleTaskModalClose = () => {
		setPhaseTaskRecord(null);
	};

	const handleDeliverableFormUpdate = (payload: any = null) => {
		setDeliverableRecord(payload);
	};

	const handleDeliverableModalClose = () => {
		setDeliverableModalOpen(false);
		setDeliverableRecord(null);
	};

	return (
		<>
			<CRUDTabTableModal
				items={items}
				tabKey={"phase"}
				otherData={currentTemplateData}
				user={userInfo.user}
				reorderModal={
					formOption === "general_multi_asset_template_operation" ? false : true
				}
				userPermissions={
					formOption === "general_multi_asset_template_operation"
						? ["update"]
						: null
				}
			/>
			{phaseTaskRecord?._id && (
				<TaskConfigurationModal
					open={taskModalOpen}
					setOpen={setTaskModalOpen}
					option={itemsFormOption}
					handleFormUpdate={handleTaskFormUpdate}
					handleModalClose={handleTaskModalClose}
					setDeliverableModalOpen={setDeliverableModalOpen}
					setDeliverableRecord={setDeliverableRecord}
					record={phaseTaskRecord}
					allCampaignTasks={allCampaignTasks}
					otherData={currentTemplateData}
					phaseID={phaseTaskRecord?._id}
					taskRecord={phaseTaskRecord?.task}
					activeRowKey={phaseTaskRecord?.task?._id}
					extraData={[]}
					user={userInfo.user}
					userRole={userInfo.userRole}
				/>
			)}
			<DeliverableConfigurationModal
				open={deliverableModalOpen}
				setOpen={setDeliverableModalOpen}
				option={itemsFormOption}
				handleFormUpdate={handleDeliverableFormUpdate}
				handleModalClose={handleDeliverableModalClose}
				otherData={currentTemplateData}
				phaseID={phaseTaskRecord?._id}
				taskID={phaseTaskRecord?.task?._id}
				deliverableRecord={deliverableRecord}
				taskRecord={phaseTaskRecord?.task}
				activeRowKey={deliverableRecord?._id}
				extraData={[]}
				user={userInfo.user}
				userRole={userInfo.userRole}
			/>
		</>
	);
}

export default ProgrammeSettingsWorkflowConfig;
